const STORAGE_KEY = 'attribution_v1';
const TTL_MS = 90 * 86400 * 1000;
const MAX_UTM_LEN = 500;
const MAX_REFERRER_LEN = 2048;

export interface AttributionPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  referrer_url?: string;
  device_fingerprint?: string;
}

interface StoredAttribution extends AttributionPayload {
  captured_at: number;
}

function safeRead(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || typeof parsed.captured_at !== 'number') {
      return null;
    }
    return parsed as StoredAttribution;
  } catch {
    return null;
  }
}

function safeWrite(data: StoredAttribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* localStorage blocked — silent fail */
  }
}

function trunc(value: string | null, max: number): string | undefined {
  if (!value) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

export function captureAttribution(): void {
  try {
    const existing = safeRead();
    const fresh = existing && Date.now() - existing.captured_at < TTL_MS;

    const params = new URLSearchParams(window.location.search);
    // Accept both the new opaque referral_code (alphanumeric, ≤16 chars,
    // matches the User.referral_code column) and a legacy numeric user
    // id, both of which the backend resolves in populate_user_attribution.
    const rawRef = params.get('ref');
    const ref =
      rawRef && /^[A-Za-z0-9_-]{1,16}$/.test(rawRef) ? rawRef : undefined;

    // Special case for referrals: a ref-link that arrives AFTER the user
    // has already touched the site (UTM tracked, no ref) must still bind
    // them to the partner. UTM stays first-touch (industry default), but
    // ref upgrades to last-touch — overwriting only the ref field on an
    // existing fresh entry instead of being thrown away by the TTL guard.
    if (fresh && ref && existing!.ref !== ref) {
      safeWrite({ ...existing!, ref, captured_at: Date.now() });
      return;
    }
    if (fresh) {
      // First-touch — keep what we have for UTM/referrer
      return;
    }

    const utm_source = trunc(params.get('utm_source'), MAX_UTM_LEN);
    const utm_medium = trunc(params.get('utm_medium'), MAX_UTM_LEN);
    const utm_campaign = trunc(params.get('utm_campaign'), MAX_UTM_LEN);
    const utm_content = trunc(params.get('utm_content'), MAX_UTM_LEN);
    const utm_term = trunc(params.get('utm_term'), MAX_UTM_LEN);

    const referrer_url = trunc(document.referrer || null, MAX_REFERRER_LEN);

    const hasSignal = !!(utm_source || utm_medium || utm_campaign ||
                        utm_content || utm_term || ref || referrer_url);
    if (!hasSignal) return;

    safeWrite({
      utm_source, utm_medium, utm_campaign, utm_content, utm_term,
      ref, referrer_url,
      captured_at: Date.now(),
    });
  } catch {
    /* never throw from attribution capture */
  }
}

export function getAttributionPayload(): AttributionPayload {
  const stored = safeRead();
  if (!stored) return {};
  const { captured_at, ...payload } = stored;
  // Strip undefined keys so JSON serializer omits them
  return Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined && v !== null)
  ) as AttributionPayload;
}

export async function getAttributionPayloadWithFingerprint(): Promise<AttributionPayload> {
  const base = getAttributionPayload();
  try {
    const { getDeviceFingerprint } = await import('./deviceFingerprint');
    const fp = await getDeviceFingerprint();
    if (fp) {
      base.device_fingerprint = fp;
    }
  } catch {
    /* never throw from attribution */
  }
  return base;
}

export function clearAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
